import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MotorFiscalLookupService } from '@shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { MaskService } from '@shared/services/mask.service';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { CstIpiStruct } from '@shared/services/structs/motor-fiscal/cst-ipi.struct';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { getCstIpiTexto, CstIpiTexto } from '@shared/constants/motor-fiscal/cst-ipi-metadata';
import {
    IndicadoresCst,
    MapaCamposIndicador,
    isAlgumCampoAtivo,
    isCampoAtivo,
    isCodigoDaOperacao,
    lerIndicadoresCst,
    limparCamposInativos,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';

/** Campo do indicador do CST do IPI -> control da regra da natureza (aba e wizard de exceção usam os mesmos nomes). */
export const MAPA_CAMPOS_IPI_NATUREZA: MapaCamposIndicador = {
    pIPI: 'pIpi',
    vUnid: 'vUnid',
};

@Component({
    selector: 'app-ipi-tributo-regra-section',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatBadgeModule,
        MatIconModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        AlertCardComponent,
    ],
    templateUrl: './tributo-regra-section.component.html',
    styleUrl: './tributo-regra-section.component.css',
})
export class IpiTributoRegraSectionComponent implements OnChanges {
    @Input({ required: true }) form!: FormGroup;
    @Input() codigoTributos: CstIpiStruct[] = [];
    @Input() variaveis: VariavelStruct[] = [];
    @Input() personalizadasCount = 0;
    @Input() modoExcecao = false;
    @Input() personalizarDisabled = false;
    /** Tipo de operação da natureza: Entrada ou Saída. Define a faixa de CST permitida. */
    @Input() tipoOperacao: TipoOperacaoEnum = TipoOperacaoEnum.Saida;
    @Output() personalizar = new EventEmitter<void>();

    public readonly percentMask = this.maskService.getPercentMask();
    public readonly currencyMask = this.maskService.getCurrencyMask();

    /** Só os CSTs da operação (entrada ou saída) da natureza, pelo indicador. */
    public get codigoTributosDoTipoOperacao(): CstIpiStruct[] {
        return this.codigoTributos.filter(c => isCodigoDaOperacao(c.indicadores, this.tipoOperacao));
    }

    public get indicadores(): IndicadoresCst {
        const cstIpi = this.form?.get('cstIpi')?.value ?? null;
        return lerIndicadoresCst(this.codigoTributos.find(c => c.codigo === cstIpi)?.indicadores);
    }

    public get texto(): CstIpiTexto | null {
        const cstIpi = this.form?.get('cstIpi')?.value ?? null;
        return getCstIpiTexto(cstIpi, this.codigoTributos.find(c => c.codigo === cstIpi)?.descricao, this.indicadores);
    }

    public ativo(chave: string): boolean {
        return isCampoAtivo(this.indicadores, chave);
    }

    public algumAtivo(chaves: string[]): boolean {
        return isAlgumCampoAtivo(this.indicadores, chaves);
    }

    @Input() modalidadesBcSt: ModalidadeBcOption[] = [];
    public codigosEnquadramentoIpi: LookupItemStruct[] = [];

    constructor(
        private maskService: MaskService,
        private motorFiscalLookupService: MotorFiscalLookupService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['form'] && this.form) {
            this.carregarCodigosEnquadramentoIpi(this.form.get('cstIpi')?.value ?? null);
            this.form.get('cstIpi')?.valueChanges.subscribe(cstIpi => {
                this.carregarCodigosEnquadramentoIpi(cstIpi ?? null);
                // Zera os campos que o novo CST não exibe (evita valor obsoleto no payload).
                limparCamposInativos(this.form, this.indicadores, MAPA_CAMPOS_IPI_NATUREZA);
            });
            this.form.get('isAtivo')?.valueChanges.subscribe(() => this.sincronizarObrigatoriedadeCEnq());
        }

        // Trocar o tipo de operação troca a faixa de CST permitida: um código da faixa
        // anterior deixaria de aparecer no select e não pode continuar selecionado.
        if (changes['tipoOperacao'] && !changes['tipoOperacao'].firstChange && this.form) {
            const control = this.form.get('cstIpi');
            const indicadores = this.codigoTributos.find(c => c.codigo === control?.value)?.indicadores;
            if (control?.value && !isCodigoDaOperacao(indicadores, this.tipoOperacao)) {
                control.setValue(null);
                this.onCstIpiSelectionChange();
            }
        }
    }

    private carregarCodigosEnquadramentoIpi(cstIpi: string | null): void {
        if (!cstIpi) {
            this.codigosEnquadramentoIpi = [];
            this.sincronizarObrigatoriedadeCEnq();
            return;
        }

        this.motorFiscalLookupService.getCodigosEnquadramentoIpi(cstIpi).subscribe(list => {
            if (this.form.get('cstIpi')?.value === cstIpi) {
                this.codigosEnquadramentoIpi = list;
                this.sincronizarObrigatoriedadeCEnq();
            }
        });
    }

    public onCstIpiSelectionChange(): void {
        this.codigosEnquadramentoIpi = [];
        this.form.get('codigoEnquadramentoIpi')?.setValue(null);
        this.sincronizarObrigatoriedadeCEnq();
    }

    public get cEnqObrigatorio(): boolean {
        return this.codigosEnquadramentoIpi.length > 0
            && (this.modoExcecao || !!this.form.get('isAtivo')?.value);
    }

    private sincronizarObrigatoriedadeCEnq(): void {
        const control = this.form.get('codigoEnquadramentoIpi');
        this.aplicarObrigatorio(control, this.cEnqObrigatorio);
    }

    private aplicarObrigatorio(control: AbstractControl | null, obrigatorio: boolean): void {
        if (!control) return;
        if (obrigatorio) {
            control.addValidators(Validators.required);
        } else {
            control.removeValidators(Validators.required);
        }
        control.updateValueAndValidity({ emitEvent: false });
    }

    /** True quando o botão "Personalizar fórmulas" não pode ser acessado. */
    public get personalizarBloqueado(): boolean {
        return this.personalizarDisabled || !this.form?.get('cstIpi')?.value;
    }

    /** Mensagem do tooltip exibida apenas quando o botão está desabilitado. */
    public get personalizarTooltip(): string {
        if (this.personalizarDisabled) {
            return this.modoExcecao
                ? 'A personalização de fórmulas não está disponível para exceções.'
                : 'A personalização de fórmulas está indisponível no momento.';
        }
        if (!this.form?.get('cstIpi')?.value) {
            return 'Selecione um Código para habilitar a personalização de fórmulas.';
        }
        return '';
    }

    public onPersonalizar(): void {
        this.personalizar.emit();
    }
}
